import { useEffect } from 'react';
import { create } from 'zustand';
import {
  CURRENT,
  decideUpdate,
  parseManifest,
  rolloutBucket,
  verifyManifest,
  type DeferReason,
  type ReleaseManifest,
  type UpdateUrgency,
} from './releaseChannel.js';

/**
 * Update scheduling.
 *
 * The rule that shapes everything here: **an update must never interrupt the
 * user.** Reloading the page mid-sentence or mid-call is a worse failure than
 * running yesterday's build for another ten minutes. So the client separates
 * three things that are usually conflated:
 *
 *   1. *Discovering* an update    — cheap, frequent, invisible.
 *   2. *Offering* an update       — a quiet affordance, never a takeover.
 *   3. *Applying* an update       — only when demonstrably safe, or when the
 *                                    user explicitly asks for it.
 *
 * Blockers register themselves (`registerBlocker`) rather than being polled, so
 * a new interruption-sensitive feature — a call, an upload in flight, an
 * unfinished form — becomes safe by declaring itself, not by remembering to
 * edit this file.
 */

const MANIFEST_URL = '/release.json';
const POLL_INTERVAL_MS = 15 * 60_000;
const MIN_RECHECK_MS = 2 * 60_000;
const BACKOFF_START_MS = 30_000;
const BACKOFF_MAX_MS = 10 * 60_000;
/** How long the app must be hidden before a pending update applies itself. */
const IDLE_AUTO_APPLY_MS = 5 * 60_000;

/** Public key for release signatures. Empty in dev; see docs/UPDATES.md. */
const RELEASE_PUBLIC_KEY: string | undefined = import.meta.env['VITE_RELEASE_PUBKEY'];

type Blocker = () => DeferReason | null;

interface UpdateStore {
  urgency: UpdateUrgency;
  manifest: ReleaseManifest | null;
  checking: boolean;
  lastCheckedAt: number | null;
  deferredReason: DeferReason | null;
  dismissed: boolean;

  check: (reason: 'startup' | 'interval' | 'focus' | 'online' | 'manual') => Promise<void>;
  apply: (force?: boolean) => void;
  dismiss: () => void;
}

const blockers = new Set<Blocker>();

/**
 * Declares that applying an update right now would interrupt something.
 * Returns an unregister function; call it when the activity ends.
 */
export function registerBlocker(blocker: Blocker): () => void {
  blockers.add(blocker);
  return () => {
    blockers.delete(blocker);
  };
}

function firstBlocker(): DeferReason | null {
  for (const blocker of blockers) {
    const reason = blocker();
    if (reason !== null) return reason;
  }
  return null;
}

let failureCount = 0;
let inFlight: Promise<void> | null = null;

export const useUpdateStore = create<UpdateStore>((set, get) => ({
  urgency: 'none',
  manifest: null,
  checking: false,
  lastCheckedAt: null,
  deferredReason: null,
  dismissed: false,

  check: async (reason) => {
    // Collapse concurrent checks: focus + online + interval can all fire at once
    // after a laptop wakes up, and three simultaneous fetches tell us nothing
    // three times.
    if (inFlight !== null) return inFlight;

    const { lastCheckedAt } = get();
    if (
      reason !== 'manual' &&
      lastCheckedAt !== null &&
      Date.now() - lastCheckedAt < MIN_RECHECK_MS
    ) {
      return;
    }

    set({ checking: true });

    inFlight = (async () => {
      try {
        // `cache: 'no-store'` matters: a cached manifest makes the client
        // permanently blind to updates, which is the classic failure of this
        // whole pattern.
        const response = await fetch(MANIFEST_URL, {
          cache: 'no-store',
          credentials: 'omit',
          headers: { accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`manifest ${response.status}`);

        const manifest = parseManifest(await response.json());
        if (manifest === null) throw new Error('malformed manifest');

        const signatureStatus = await verifyManifest(manifest, RELEASE_PUBLIC_KEY);
        const urgency = decideUpdate({
          manifest,
          currentBuildId: CURRENT.buildId,
          currentVersion: CURRENT.version,
          bucket: rolloutBucket(),
          signatureStatus,
        });

        failureCount = 0;
        set((s) => ({
          manifest,
          urgency,
          lastCheckedAt: Date.now(),
          // A newly-discovered release re-opens a prompt the user dismissed for
          // an older one, but re-dismissing the *same* release stays dismissed.
          dismissed: s.manifest?.buildId === manifest.buildId ? s.dismissed : false,
        }));

        if (urgency === 'required') get().apply(true);
      } catch {
        // A failed check is not an error the user needs to see. It is normal
        // while offline, and the next check will succeed.
        failureCount += 1;
        set({ lastCheckedAt: Date.now() });
      } finally {
        set({ checking: false });
        inFlight = null;
      }
    })();

    return inFlight;
  },

  apply: (force = false) => {
    const blocked = firstBlocker();
    if (blocked !== null && !force) {
      set({ deferredReason: blocked });
      return;
    }
    // Drafts are persisted by the composer on every keystroke, so a reload is
    // lossless. Nothing is flushed here — a save that only happens on the
    // update path is a save that is never tested.
    window.location.reload();
  },

  dismiss: () => set({ dismissed: true, deferredReason: 'user-postponed' }),
}));

/**
 * Wires the store to the browser lifecycle. Mount once, near the root.
 */
export function useUpdateChecker(): void {
  const check = useUpdateStore((s) => s.check);

  useEffect(() => {
    /*
     * On launch, apply immediately rather than offering a banner.
     *
     * A fresh start has nothing to interrupt — no draft, no call, no half-typed
     * anything — so the polite deferral the rest of this file is built around
     * buys nothing and costs a person an extra click on the one screen where
     * the answer is obviously yes.
     *
     * Guarded against a loop. If the manifest advertises a build the server is
     * not yet serving, reloading would find the same stale bundle and reload
     * again forever. One marker per tab means the worst case is a single
     * wasted reload rather than an unusable app.
     */
    void check('startup').then(() => {
      const { urgency, manifest } = useUpdateStore.getState();
      if (urgency === 'none' || manifest === null) return;
      if (firstBlocker() !== null) return;

      const key = 'aksolot.update.applied';
      if (sessionStorage.getItem(key) === manifest.buildId) return;
      sessionStorage.setItem(key, manifest.buildId);
      useUpdateStore.getState().apply();
    });

    let timer: number | undefined;
    const schedule = () => {
      window.clearTimeout(timer);
      // Back off after failures so an offline client does not hammer the
      // network every 15 minutes for hours.
      const delay =
        failureCount === 0
          ? POLL_INTERVAL_MS
          : Math.min(BACKOFF_START_MS * 2 ** (failureCount - 1), BACKOFF_MAX_MS);
      timer = window.setTimeout(() => {
        void check('interval').finally(schedule);
      }, delay);
    };
    schedule();

    const onFocus = () => void check('focus');
    const onOnline = () => void check('online');

    let hiddenSince: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
      }
      const away = hiddenSince === null ? 0 : Date.now() - hiddenSince;
      hiddenSince = null;
      void check('focus');

      // Applied on *return* rather than while hidden: reloading a background
      // tab silently discards whatever the user left on screen, and they come
      // back to a page that lost its place.
      const { urgency } = useUpdateStore.getState();
      if (urgency === 'optional' && away > IDLE_AUTO_APPLY_MS && firstBlocker() === null) {
        useUpdateStore.getState().apply();
      }
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);
}
